// What the phone still has to fetch, and the order to fetch it in.
//
// The build ships a recording for every word with a card of its own — 40 MB, so the word decks,
// the drills and the placement test work from the moment the app installs. It does NOT ship the
// sentence recordings: 139 MB, which is not an app download (docs/audio-on-the-phone.md).
//
// THE ORDER IS THE WHOLE POINT. A learner who has just installed the app is weeks away from the
// eight-word sentences, so the download follows the decks in the order the app offers them and is
// racing ahead of the learner rather than blocking them. Nothing here waits for anything: a
// sentence whose clip has not landed speaks in the system voice, exactly as it does today.

import type { Sentences, Stories, Vocab } from './types.js';

/**
 * Every clip the phone build lacks, in the order it should ask for them.
 *
 * A sentence brings its own words with it. Tapping a word inside a sentence speaks that word, and
 * 1,481 of those words have no card of their own and so no recording in the bundle — fetching them
 * beside their sentence means the sentence is never half-audible.
 *
 * Deduplicated by first appearance: the decks overlap heavily (a four-word sentence about school
 * is in len-4 AND topic-school), and the first deck to want a clip is the one that decides when it
 * arrives.
 */
export function planFetch(vocab: Vocab, sentences: Sentences, stories?: Stories): string[] {
  const cardWords = new Set(vocab.words.map((w) => w.s));
  const plan: string[] = [];
  const seen = new Set<string>();

  const add = (clip: string): void => {
    if (seen.has(clip)) return;
    seen.add(clip);
    plan.push(clip);
  };

  // The stories first, and they are a handful of clips rather than a section of the download.
  //
  // Their LINES already ship — eighty of them, a megabyte — so the read-along works out of the
  // box. What does not ship is the words inside them that have no card of their own, and in a
  // read-along every word is tappable. Ten clips to finish a feature that is otherwise already
  // there is the best-value thing in the whole plan, so it goes at the front.
  for (const story of stories?.stories ?? []) {
    for (const line of story.lines) {
      for (const w of line.w) if (!cardWords.has(w.t)) add(w.t);
    }
  }

  for (const deck of sentences.decks) {
    for (const id of deck.sentences) {
      const sentence = sentences.sentences[id];
      if (!sentence) continue;
      add(`s/${sentence.tid}`);
      for (const w of sentence.w) if (!cardWords.has(w.t)) add(w.t);
    }
  }
  return plan;
}

/** Where a clip lives, under whatever origin serves them. */
export function clipPath(clip: string, format: string, prefix = 'audio/'): string {
  // A word is filed under the characters it speaks, so its name has to be encoded; a sentence's
  // name carries a "/" that must survive as a path separator. Same rule as playback.
  //
  // The prefix comes from the manifest rather than being written here, because it names a PAIR —
  // audio/zh-en/ — and this file is the same file in every build. It defaults to the old flat
  // layout so a manifest written before MYN-114 still resolves.
  return `${prefix}${clip.split('/').map(encodeURIComponent).join('/')}.${format}`;
}

export interface FetchProgress {
  /** Clips in the plan that are now on this device. */
  done: number;
  /** Clips in the plan, total. */
  total: number;
  /** Bytes written to this device. Counted as they land, not estimated. */
  bytes: number;
  /** What the whole job weighs, from the manifest. Zero when the build did not say. */
  totalBytes: number;
}

/** Whole percent, and never 100 until it really is — a progress bar that lies is worse than none. */
export function percent(p: FetchProgress): number {
  if (p.total === 0) return 100;
  if (p.done >= p.total) return 100;
  return Math.min(99, Math.floor((100 * p.done) / p.total));
}

/** Megabytes, to one decimal, the way a download is normally quoted. */
export const mb = (bytes: number): string => `${(bytes / 1e6).toFixed(1)} MB`;
